import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useEffect, useMemo, useState } from "react";

import {
  AttachFileOutlined,
  CloseOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import SendOutlined from "@mui/icons-material/SendOutlined";


import { IconButton } from "@mui/material";
import { isEmpty, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useLoanTermsHook } from "../../hooks/useLoanTermsHook";
import { sidebarClear } from "../../redux/actions/sidebarActions";
import LoanEmailRecipientSelector from "../common/Loan/LoanEmailRecipientSelector";
import HtmlEditor from "../html/HtmlEditor";
import TextInput from "../inputs/TextInput";
import Flx from "../layout/Flx";
import SelectableLoanFilesTable from "../tables/SelectableLoanFilesTable";
import Txt from "../typography/Txt";

// ─── Constants ────────────────────────────────────────────────────────────────

const DIVIDER = "rgba(0,0,0,0.08)";

// ─── Main Component ───────────────────────────────────────────────────────────

const GenericEmailEditor = ({ template }) => {
  //   console.log("GenericEmailEditor:", template);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const dispatch = useDispatch();
  const { sendEmailTemplate, loading } = useLoanTermsHook();
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [showCC, setShowCC] = useState(false);
  const [subject, setSubject] = useState(null);
  const [contentType, setContentType] = useState("html"); // 'html' or 'text'
  const [emailBody, setEmailBody] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const onHtmlEditorChange = (v) => {
    setEmailBody(v);
  };

  const handleOnSuccess = () => {
    setTo([]);
    setCc([]);
    setShowCC(false);
    setSubject(null);
    setAttachments([]);
    setEmailBody("");
    setContentType("html");
    dispatch(sidebarClear());
  };

  const handleSend = () => {
    const payload = {
      toRecipients: to,
      ccRecipients: cc,
      subject: subject,
      body: emailBody?.htmlString || emailBody,
      attachmentIds: attachments?.map((a) => a._id) || [],
      contentType: contentType || "html",
    };

    sendEmailTemplate({
      dealId: loanDrilldown?._id,
      templateName: template?.template_name,
      data: payload,
      onSuccessFn: () => handleOnSuccess(),
    });
  };
  useEffect(() => {
    // setEmailBody(template?.text_body || template?.text || "");
    setEmailBody(template?.html_body || template?.text || "");
    setSubject(template?.subject || "");
  }, [template]);

  return (
    <Box>
      {/* To / Cc / Subject — structured field rows */}
      <Box
        sx={{
          mb: 1.5,
          border: `1px solid ${DIVIDER}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        <HeaderInput label="To:" divider>
          <Flx ac sx={{ position: "relative" }}>
            <LoanEmailRecipientSelector
              value={to}
              onChange={setTo}
              sx={{ pr: "14px" }}
            />
            {!showCC && (
              <Button
                variant="text"
                sx={{
                  p: 0,
                  flexShrink: 0,
                  minWidth: 0,
                  textDecoration: "underline",
                  fontSize: "11px",
                  position: "absolute",
                  right: "12px",
                  //   ml: 1,
                }}
                onClick={() => setShowCC(true)}
              >
                Cc
              </Button>
            )}
          </Flx>
        </HeaderInput>
        {showCC && (
          <HeaderInput label="Cc:" divider>
            <Flx ac sx={{ position: "relative" }}>
              <LoanEmailRecipientSelector
                sx={{ pr: "14px" }}
                value={cc}
                onChange={setCc}
              />
              <IconButton
                size="small"
                sx={{
                  // p: 0,
                  flexShrink: 0,
                  minWidth: 0,
                  textDecoration: "underline",
                  fontSize: "11px",
                  position: "absolute",
                  right: "5px",
                  //   ml: 1,
                }}
                onClick={() => {
                  setCc([]);
                  setShowCC(false);
                }}
              >
                <CloseOutlined sx={{ fontSize: "16px !important" }} />
              </IconButton>
            </Flx>
          </HeaderInput>
        )}
        <HeaderInput label="Subject:">
          <TextInput
            value={subject}
            onChange={(val) => setSubject(val)}
            placeholder="Email subject..."
            sx={{ "& fieldset": { border: "none" } }}
          />
        </HeaderInput>
      </Box>

      {/* Body Editor */}
      <Box
        sx={{
          border: `1px solid ${DIVIDER}`,
          borderRadius: "8px",
          overflow: "hidden",
          //   mb: 1.5,
          //   p: 2,
          //   background: "#fafbfc",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          ".rdw-editor-toolbar": {
            backgroundColor: "#fafbfc !important",
            // backgroundColor: "none",
          },
        }}
      >
        {/* <TabSwitcher
          value={contentType}
          onChange={onContentTypeChange}
          tabs={[
            { label: "HTML", value: "html" },
            { label: "Plain Text", value: "text" },
          ]}
        /> */}
        <HtmlEditor
          maxHeight="unset"
          backgroundColor={"#ffffff"}
          sx={{ minHeight: "200px" }}
          value={emailBody}
          //   readOnly
          onChange={onHtmlEditorChange}
          suppressOuterBorder
          placeholder="Start typing here..."
        />
      </Box>

      <Box
        sx={{
          border: `1px solid ${DIVIDER}`,
          borderRadius: "8px",
          overflow: "hidden",
          mb: 2,
          mt: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <EmailAttachmentsSelector
          attachments={attachments}
          setAttachments={setAttachments}
        />
      </Box>

      {/* Footer: count + send */}
      <Flx ac g={1} end fw>
        <Txt sx={{ fontSize: "12px", color: "#94a3b8" }}>
          {attachments.length > 0
            ? `${attachments.length} attachment${attachments.length !== 1 ? "s" : ""} selected`
            : "No attachments"}
        </Txt>
        <Button
          endIcon={<SendOutlined />}
          onClick={handleSend}
          disabled={isEmpty(to) || isEmpty(subject)}
          variant="contained"
          loading={loading}
          size="large"
        >
          Send Email
        </Button>
      </Flx>
    </Box>
  );
};

// ─── Recipient Selector ───────────────────────────────────────────────────────


// ─── Attachments Selector ─────────────────────────────────────────────────────

const EmailAttachmentsSelector = ({ attachments, setAttachments }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = (loanDrilldown?.loanDocuments || []).filter(
    (d) => !d?.isHidden,
  );
  const [minimized, setMinimized] = useState(true);
  //   console.log("attachments", attachments);
  const attachmentOptions = useMemo(() => {
    return loanDocuments
      .map((a) => ({
        label: a?.file_display_name || "",
        value: a?._id,
        docType: a?.docType,
        uploadDate: a?.uploadDate,
        _id: a?._id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [loanDocuments]);


  return (
    <Box>
      {/* Section label */}
      <Box
        onClick={() => setMinimized(!minimized)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          cursor: "pointer",
          background: "#fafbfc",
          borderBottom:
            attachmentOptions.length > 0 ? `1px solid ${DIVIDER}` : "none",
          ":&:hover": { background: "#f8fafc" },
        }}
      >
        <Flx ac gap={1}>
          <AttachFileOutlined sx={{ fontSize: 14, color: "#94a3b8" }} />
          <Txt
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Attached Documents ({size(attachments)})
          </Txt>
        </Flx>

        {!minimized ? (
          <ExpandLessOutlined sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMoreOutlined sx={{ fontSize: 18 }} />
        )}
      </Box>

      {minimized ? null : (
        <SelectableLoanFilesTable
          height="400px"
          files={loanDocuments}
          selectedRows={attachments}
          setSelectedRows={setAttachments}
        />
      )}
    </Box>
  );
};

// ─── Header Input Row ─────────────────────────────────────────────────────────

const HeaderInput = ({ label, icon, g = 1, children, sx, divider }) => {
  return (
    <Flx
      ac
      gap={g}
      sx={{
        py: 0.25,
        background: "#ffffff",
        borderBottom: divider ? `1px solid ${DIVIDER}` : "none",
        "input, .MuiChip-label": {
          fontSize: "12px",
        },
        ...sx,
      }}
    >
      <Flx
        ac
        g={1}
        sx={{
          minWidth: 72,
          pl: 1.5,
          flexShrink: 0,
          borderRight: `1px solid ${DIVIDER}`,
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          background: "#fafbfc",
        }}
      >
        {icon}
        <Txt
          sx={{
            color: "#64748b",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </Txt>
      </Flx>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Flx>
  );
};

export default GenericEmailEditor;
